import React, { useEffect, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { stringToColor, cn } from '@/lib/utils';
import ProfileHalftoneHalo from '../ProfileHalftoneHalo';

import placeholder1 from '@/assets/profile-placeholder-1.png';
import placeholder2 from '@/assets/profile-placeholder-2.png';
import placeholder3 from '@/assets/profile-placeholder-3.png';
import placeholder4 from '@/assets/profile-placeholder-4.png';
import placeholder5 from '@/assets/profile-placeholder-5.png';

interface InterviewMediaProps {
  intervieweeName: string;
  photoUrl?: string;
  photoUrl2?: string; // Second interviewee profile picture
  className?: string;
  interviewId?: string; // Add interview ID for special cases
}

const InterviewMedia: React.FC<InterviewMediaProps> = ({
  intervieweeName,
  photoUrl,
  photoUrl2,
  className,
  interviewId,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageError2, setImageError2] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoading2, setIsLoading2] = useState(true);
  const [cachedUrl, setCachedUrl] = useState<string | undefined>(undefined);
  const [cachedUrl2, setCachedUrl2] = useState<string | undefined>(undefined);
  const [attemptCount, setAttemptCount] = useState(0);
  
  const initials = (intervieweeName || '')
    .split(' ')
    .map(name => name[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);

  const bgColor = stringToColor(intervieweeName || '');
  
  const hashCode = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  };
  
  const placeholders = [placeholder1, placeholder2, placeholder3, placeholder4, placeholder5];
  const selectedPlaceholder = placeholders[Math.abs(hashCode(intervieweeName || '') % placeholders.length)];
  
  useEffect(() => {
    if (photoUrl !== undefined) {
      setImageError(false);
      setIsLoading(true);
      setAttemptCount(0);
    }
  }, [photoUrl, intervieweeName]);
  
  // Reset state for photoUrl2 when it changes
  useEffect(() => {
    if (photoUrl2 !== undefined) {
      setImageError2(false);
      setIsLoading2(true);
    }
  }, [photoUrl2]);
  
  useEffect(() => {
    // Special case for Macron article - always use the specific profile image
    if (interviewId === 'bfe73dc3-a63f-4ef1-b9da-3e3df357c1af') {
      const macronPhotoUrl = '/special-profiles/macron-article-profile.webp';
      console.log('[InterviewMedia] Using special Macron photo:', macronPhotoUrl);
      setCachedUrl(macronPhotoUrl);
      setIsLoading(true);
      setImageError(false);
      return;
    }

    if (!photoUrl) {
      console.log('[InterviewMedia] No photoUrl provided for:', intervieweeName);
      setCachedUrl('');
      setIsLoading(false);
      return;
    }

    // Fix URL normalization
    let normalizedUrl = photoUrl;
    if (photoUrl.startsWith('http://')) {
      normalizedUrl = `https://${photoUrl.slice(7)}`;
    }

    console.info('[InterviewMedia] Using image URL:', { intervieweeName, url: normalizedUrl });
    console.log('[InterviewMedia DEBUG] Full photoUrl data:', {
      original: photoUrl,
      normalized: normalizedUrl,
      intervieweeName,
      hasPhotoUrl: !!photoUrl,
      photoUrlLength: photoUrl?.length || 0
    });
    
    setCachedUrl(normalizedUrl);
    setIsLoading(true);
    setImageError(false);
  }, [photoUrl, intervieweeName, interviewId]);
  
  // Process photoUrl2 similarly
  useEffect(() => {
    if (!photoUrl2) {
      setCachedUrl2('');
      setIsLoading2(false);
      return;
    }

    let normalizedUrl2 = photoUrl2;
    if (photoUrl2.startsWith('http://')) {
      normalizedUrl2 = `https://${photoUrl2.slice(7)}`;
    }

    console.info('[InterviewMedia] Using second image URL:', { intervieweeName, url: normalizedUrl2 });
    setCachedUrl2(normalizedUrl2);
    setIsLoading2(true);
    setImageError2(false);
  }, [photoUrl2, intervieweeName]);

  const handleImageError = () => {
    console.error('[InterviewMedia] Image failed to load:', { 
      intervieweeName, 
      url: cachedUrl,
      originalPhotoUrl: photoUrl,
      error: 'Failed to load image'
    });
    setImageError(true);
    setIsLoading(false);
  };
  
  const handleImageError2 = () => {
    console.error('[InterviewMedia] Second image failed to load:', { 
      intervieweeName, 
      url: cachedUrl2,
      originalPhotoUrl2: photoUrl2,
      error: 'Failed to load second image'
    });
    setImageError2(true);
    setIsLoading2(false);
  };

  // Get the best available image source
  const getAvatarSrc = () => {
    if (cachedUrl && !imageError) return cachedUrl;
    if (imageError) return selectedPlaceholder;
    return selectedPlaceholder; // fallback
  };
  
  // Get second avatar source (use different placeholder)
  const getAvatarSrc2 = () => {
    if (cachedUrl2 && !imageError2) return cachedUrl2;
    // Use a different placeholder for second image
    const secondPlaceholderIndex = (Math.abs(hashCode(intervieweeName || '') % placeholders.length) + 1) % placeholders.length;
    if (imageError2) return placeholders[secondPlaceholderIndex];
    return placeholders[secondPlaceholderIndex];
  };
  
  // Check if we have a valid second photo
  const hasSecondPhoto = !!photoUrl2 && photoUrl2.trim() !== '';

  // Render two overlapping profile pictures if photoUrl2 is provided
  if (hasSecondPhoto) {
    return (
      <div className={cn(className, "profile-container relative")}>
        <div className="relative" style={{ width: '288px', height: '288px' }}>
          {/* First profile - positioned top-left, scaled down */}
          <div className="absolute -top-2 -left-2 z-10" style={{ transform: 'scale(0.7)', transformOrigin: 'bottom right' }}>
            <ProfileHalftoneHalo
              avatarSrc={getAvatarSrc()}
              size={288}
              pulse
              className="profile-halo"
              onImageLoad={() => setIsLoading(false)}
              onImageError={handleImageError}
            />
          </div>
          {/* Second profile - positioned bottom-right, scaled down */}
          <div className="absolute bottom-0 right-0 z-20" style={{ transform: 'scale(0.7)', transformOrigin: 'top left' }}>
            <ProfileHalftoneHalo
              avatarSrc={getAvatarSrc2()}
              size={288}
              pulse
              className="profile-halo"
              onImageLoad={() => setIsLoading2(false)}
              onImageError={handleImageError2}
            />
          </div>
        </div>
      </div>
    );
  }

  // Single photo layout (existing behavior)
  return (
    <div className={cn(className, "profile-container relative")}>
      <ProfileHalftoneHalo
        avatarSrc={getAvatarSrc()}
        size={288}
        pulse
        className="profile-halo"
        onImageLoad={() => setIsLoading(false)}
        onImageError={handleImageError}
      />
    </div>
  );
};

export default InterviewMedia;